/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Friday, 5th April 2019
 *
 * Manages the state of the Registration process.
 */
import { observable, decorate, action, computed } from 'mobx';
import { firebase } from 'containers/services/Firebase';
import { getUserInitials } from 'utils';
import uuid from 'uuid';

class RegistrationStore {
  invite = false;
  inviteId = null;
  validInvite = false;
  inviteCheckCount = 0;
  inviteDetails = {};
  typeEnum = ['vendor', 'practice'];
  typeMap = {
    vendor: { index: 'vendor', name: 'Vendor' },
    practice: { index: 'practice', name: 'Medical Practice' },
  };
  registrationId = '';
  currentStep = 0;
  type = '';
  userFirstName = '';
  userFirstNameIsInvalid = true;
  userLastName = '';
  userLastNameIsInvalid = true;
  userEmail = '';
  userEmailIsInvalid = true;
  userPhone = '';
  userPhoneIsInvalid = true;
  userAddress1 = '';
  userAddress1IsInvalid = true;
  userAddress2 = '';
  userCity = '';
  userCityIsInvalid = true;
  userState = '';
  userStateIsInvalid = true;
  userZip = '';
  userZipIsInvalid = true;
  userAddressComponents = {};
  userBio = '';
  userAvatarUrl = '';
  userAvatarFile = null;
  userAvatarResponse = {};
  userAvatarDownloadURL = '';
  companyName = '';
  companyNameIsInvalid = true;
  companyPhone = '';
  companyPhoneIsInvalid = true;
  companyAddress1 = '';
  companyAddress1IsInvalid = true;
  companyAddress2 = '';
  companyCity = '';
  companyCityIsInvalid = true;
  companyState = '';
  companyStateIsInvalid = true;
  companyZip = '';
  companyZipIsInvalid = true;
  companyPlaceDetails = {};
  companyLogoUrl = '';
  companyLogoFile = null;
  companyLogoResponse = null;
  companyWebsite = '';
  companyUtcOffset = '';
  companyBio = '';
  companyBioIsInvalid = true;
  accountPassword = '';
  showPasswordText = false;
  isLoading = true;
  isRegistering = false;
  accountEmailSent = false;
  accountExists = false;

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.registrationId = uuid.v4();
  }

  get isUserProfileComplete() {
    return this.type === 'vendor'
      ? !this.userFirstNameIsInvalid &&
          !this.userLastNameIsInvalid &&
          !this.userEmailIsInvalid &&
          !this.userPhoneIsInvalid &&
          !this.userAddress1IsInvalid &&
          !this.userCityIsInvalid &&
          !this.userStateIsInvalid &&
          !this.userZipIsInvalid &&
          this.userAvatarUrl !== ''
      : !this.userFirstNameIsInvalid &&
          !this.userLastNameIsInvalid &&
          !this.userEmailIsInvalid &&
          !this.userPhoneIsInvalid;
  }

  get isCompanyProfileComplete() {
    return (
      this.invite ||
      (!this.companyNameIsInvalid &&
        this.companyName !== '' &&
        (!this.companyPhoneIsInvalid && this.companyPhone !== '') &&
        (!this.companyAddress1IsInvalid && this.companyAddress1 !== '') &&
        (!this.companyCityIsInvalid && this.companyCity !== '') &&
        (!this.companyStateIsInvalid && this.companyState !== '') &&
        (!this.companyZipIsInvalid && this.companyZip !== ''))
    );
  }

  setField = (field, value) => (this[field] = value);

  togglePasswordText = () => {
    this.showPasswordText = !this.showPasswordText;
  };

  handlePasswordChange = e => {
    this.accountPassword = e.target.value;
  };

  handleUserInputUpdate = (event, isInvalid) => {
    this['user' + event.target.name] = event.target.value;
    this['user' + event.target.name + 'IsInvalid'] = isInvalid;
  };

  handleCompanyInputUpdate = (event, isInvalid) => {
    this['company' + event.target.name] = event.target.value;
    this['company' + event.target.name + 'IsInvalid'] = isInvalid;
  };

  handleAvatarChange = (file, response) => {
    const avatarFile = file;
    const avatarResponse = response;

    this.userAvatarUrl = avatarResponse.secure_url;
    this.userAvatarFile = avatarFile;
    this.userAvatarResponse = avatarResponse;
  };

  handleLogoChange = (file, response) => {
    this.companyLogoUrl = response.secure_url;
    this.companyLogoFile = file;
    this.companyLogoResponse = response;
  };

  handleRegistration = event => {
    event.preventDefault();
    this.setField('isRegistering', true);
    const eventDate = new Date();

    const registration = {
      id: this.registrationId,
      timestamp: eventDate,
      device: {
        thumbprint: this.rootStore.deviceStore.thumbprint,
        fingerprint: this.rootStore.deviceStore.fingerprint,
      },
      type: this.typeMap[this.type],
      user: {
        email: this.userEmail,
        password: this.accountPassword,
        firstName: this.userFirstName,
        lastName: this.userLastName,
        displayName: this.userFirstName + ' ' + this.userLastName,
        initials: getUserInitials(this.userFirstName, this.userLastName),
        phoneNumber: this.userPhone,
        address: {
          placeApiDetails: this.userAddressComponents,
          address1: this.userAddress1,
          address2: this.userAddress2,
          city: this.userCity,
          state: this.userState,
          zip: this.userZip,
        },
        bio: this.userBio,
      },
      company: {
        name: this.companyName,
        phoneNumber: this.companyPhone,
        address: {
          address1: this.companyAddress1,
          address2: this.companyAddress2,
          city: this.companyCity,
          state: this.companyState,
          zip: this.companyZip,
        },
        placeApiDetails: this.companyPlaceDetails,
        website: this.companyPlaceDetails.website || null,
        utcOffset:
          this.companyPlaceDetails.utc_offset !== undefined
            ? this.companyPlaceDetails.utc_offset
            : eventDate.getTimezoneOffset(),
        bio: this.companyBio,
      },
      invite: {
        id: this.inviteId,
        valid: this.validInvite,
        details: this.inviteDetails,
      },
    };

    // Register User - Returns New User Object
    const store = this;
    firebase
      .registerNewUser(registration)
      .then(() => firebase.auth.signInWithEmailAndPassword(store.userEmail, store.accountPassword))
      .then(credential => {
        // New User
        const currentUser = credential.user;

        // Send Verification Email
        currentUser.sendEmailVerification();

        return credential.user;
      })
      .then(user => {
        // Save User's Avatar to Storage
        const storageRef = firebase.storage.ref();

        if (store.userAvatarUrl !== '') {
          const { format, height, width, signature } = store.userAvatarResponse;
          const { lastModified, name, type, size } = store.userAvatarFile;
          try {
            const metadata = {
              contentType: 'image/' + format,
              height: height,
              width: width,
              signature: signature,
              lastModified: lastModified,
              fileName: name,
              fileType: type,
              fileSize: size,
            };

            // Fetch data from temp storage and upload to Firebase Storage

            var xhr = new XMLHttpRequest();
            xhr.responseType = 'blob';
            xhr.onload = function(event) {
              var blob = xhr.response;

              var uploadTask = storageRef
                .child('app/profileImages')
                .child(user.uid)
                .child('ProfilePicture.' + format)
                .put(blob, metadata);

              uploadTask.on(
                'state_changed',
                snapshot => {
                  // Handle Progress Here if needed
                },
                error => {
                  store.rootStore.appStore.error('system', error);
                  this.setField('isRegistering', false);
                },
                () => {
                  // Success, update download URL store reference
                  uploadTask.snapshot.ref.getDownloadURL().then(downloadURL => {
                    // Update store URL Value
                    store.userAvatarDownloadURL = downloadURL;

                    // Update User profile with image url
                    user
                      .updateProfile({
                        photoURL: downloadURL,
                      })
                      .catch(function(error) {
                        store.rootStore.appStore.error('system', error);
                        this.setField('isRegistering', false);
                      });

                    // Update User Profile
                    firebase.data
                      .ref('users')
                      .child(user.uid)
                      .child('ProfileInfo')
                      .update(
                        {
                          photoUrl: downloadURL,
                        },
                        error => {
                          if (error) {
                            store.handleError(error);
                          }
                        }
                      );
                  });
                }
              );
            };

            xhr.open('GET', store.userAvatarUrl);
            xhr.send();
          } catch (error) {
            store.handleError(error);
          }
        }
      })
      .then(() => {
        this.setField('isRegistering', false);
      })
      .catch(error => {
        // console.log('raw function error');
        // console.log(JSON.stringify(error));
        store.handleError(error);
      });
  };

  getInviteDetails = inviteId => {
    this.isLoading = true;
    this.inviteCheckCount = this.inviteCheckCount + 1;

    return new Promise((resolve, reject) => {
      firebase
        .getInviteDetails({
          id: inviteId,
        })
        .then(invite => {
          // Valid user invite
          this.setField('isLoading', false);
          this.setField('validInvite', true);
          this.setField('inviteDetails', invite.data);
          this.setField('type', 'practice');
          this.setField('userEmail', invite.data.email);
          this.setField('userEmailIsInvalid', false);
          this.setField('companyName', invite.data.company.name);
          this.setField('companyNameIsInvalid', false);
          this.setField('companyPhone', invite.data.company.phone);
          this.setField('companyPhoneIsInvalid', false);
          resolve();
        })
        .catch(error => {
          // Invalid user invite
          this.setField('isLoading', false);
          this.setField('validInvite', false);
          reject(error);
        });
    });
  };

  handleError = error => {
    this.isLoading = false;
    this.isRegistering = false;
    this.rootStore.appStore.error('user', error);
  };

  reset = () => {
    this.invite = false;
    this.inviteId = null;
    this.validInvite = false;
    this.inviteCheckCount = 0;
    this.inviteDetails = {};
    this.typeEnum = ['vendor', 'practice'];
    this.typeMap = {
      vendor: { index: 'vendor', name: 'Vendor' },
      practice: { index: 'practice', name: 'Medical Practice' },
    };
    this.registrationId = '';
    this.currentStep = 0;
    this.type = '';
    this.userFirstName = '';
    this.userFirstNameIsInvalid = true;
    this.userLastName = '';
    this.userLastNameIsInvalid = true;
    this.userEmail = '';
    this.userEmailIsInvalid = true;
    this.userPhone = '';
    this.userPhoneIsInvalid = true;
    this.userAddress1 = '';
    this.userAddress1IsInvalid = true;
    this.userAddress2 = '';
    this.userCity = '';
    this.userCityIsInvalid = true;
    this.userState = '';
    this.userStateIsInvalid = true;
    this.userZip = '';
    this.userZipIsInvalid = true;
    this.userAddressComponents = {};
    this.userBio = '';
    this.userAvatarUrl = '';
    this.userAvatarFile = null;
    this.userAvatarResponse = {};
    this.userAvatarDownloadURL = '';
    this.companyName = '';
    this.companyNameIsInvalid = true;
    this.companyPhone = '';
    this.companyPhoneIsInvalid = true;
    this.companyAddress1 = '';
    this.companyAddress1IsInvalid = true;
    this.companyAddress2 = '';
    this.companyCity = '';
    this.companyCityIsInvalid = true;
    this.companyState = '';
    this.companyStateIsInvalid = true;
    this.companyZip = '';
    this.companyZipIsInvalid = true;
    this.companyPlaceDetails = {};
    this.companyLogoUrl = '';
    this.companyLogoFile = null;
    this.companyLogoResponse = null;
    this.companyWebsite = '';
    this.companyUtcOffset = '';
    this.companyBio = '';
    this.companyBioIsInvalid = true;
    this.accountPassword = '';
    this.showPasswordText = false;
    this.isLoading = true;
    this.isRegistering = false;
    this.accountEmailSent = false;
    this.accountExists = false;
    this.rootStore.appStore.debug('Registration.resetStoreFields() [action] Registration store has been reset.');
  };
}
decorate(RegistrationStore, {
  invite: observable,
  inviteId: observable,
  validInvite: observable,
  currentStep: observable,
  type: observable,
  userFirstName: observable,
  userFirstNameIsInvalid: observable,
  userLastName: observable,
  userLastNameIsInvalid: observable,
  userEmail: observable,
  userEmailIsInvalid: observable,
  userPhone: observable,
  userPhoneIsInvalid: observable,
  userAddress1: observable,
  userAddress1IsInvalid: observable,
  userAddress2: observable,
  userCity: observable,
  userCityIsInvalid: observable,
  userState: observable,
  userStateIsInvalid: observable,
  userZip: observable,
  userZipIsInvalid: observable,
  userAddressComponents: observable,
  userBio: observable,
  userAvatarUrl: observable,
  userAvatarFile: observable,
  userAvatarResponse: observable,
  userAvatarDownloadURL: observable,
  companyName: observable,
  companyNameIsInvalid: observable,
  companyPhone: observable,
  companyPhoneIsInvalid: observable,
  companyAddress1: observable,
  companyAddress1IsInvalid: observable,
  companyAddress2: observable,
  companyCity: observable,
  companyCityIsInvalid: observable,
  companyState: observable,
  companyStateIsInvalid: observable,
  companyZip: observable,
  companyZipIsInvalid: observable,
  companyPlaceDetails: observable,
  companyLogoUrl: observable,
  companyLogoFile: observable,
  companyLogoResponse: observable,
  companyWebsite: observable,
  companyUtcOffset: observable,
  companyBio: observable,
  companyBioIsInvalid: observable,
  accountPassword: observable,
  showPasswordText: observable,
  isLoading: observable,
  isRegistering: observable,
  accountEmailSent: observable,
  accountExists: observable,
  isUserProfileComplete: computed,
  isCompanyProfileComplete: computed,
  setField: action,
  togglePasswordText: action,
  handlePasswordChange: action,
  handleUserInputUpdate: action,
  handleCompanyInputUpdate: action,
  handleAvatarChange: action,
  handleLogoChange: action,
  handleRegistration: action,
  getInviteDetails: action,
  handleError: action,
  reset: action,
});

export default RegistrationStore;
